import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import api from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import { Reveal } from '@/components/motion/Primitives';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CATEGORIES = ['All', 'Laundry Care', 'Dishwashing', 'Surface & Glass', 'Bathroom & Utility', 'Hand Wash'];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const category = params.get('category') || 'All';
    const search = params.get('search') || '';
    const [sort, setSort] = useState('featured');
    const [searchInput, setSearchInput] = useState(search);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/products', {
                params: {
                    category: category === 'All' ? undefined : category,
                    search: search || undefined,
                    sort,
                },
            });
            setProducts(data);
        } catch { setProducts([]); }
        finally { setLoading(false); }
    }, [category, search, sort]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);
    useEffect(() => { setSearchInput(search); }, [search]);

    // Debounced live search (no need to press Enter)
    useEffect(() => {
        const h = setTimeout(() => {
            const cur = params.get('search') || '';
            if (searchInput !== cur) {
                const next = new URLSearchParams(params);
                if (searchInput) next.set('search', searchInput); else next.delete('search');
                setParams(next, { replace: true });
            }
        }, 350);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const setCategory = (c) => {
        const next = new URLSearchParams(params);
        if (c === 'All') next.delete('category'); else next.set('category', c);
        setParams(next);
    };

    const submitSearch = (e) => {
        e.preventDefault();
        const next = new URLSearchParams(params);
        if (searchInput) next.set('search', searchInput); else next.delete('search');
        setParams(next);
    };

    const clearSearch = () => {
        setSearchInput('');
        const next = new URLSearchParams(params);
        next.delete('search');
        setParams(next);
    };

    return (
        <div className="relative overflow-x-clip">
            {/* Editorial header */}
            <section className="relative bg-[var(--bg-soft)] overflow-hidden">
                <div className="absolute inset-0 section-wash" />
                <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-12 lg:pt-16 pb-10">
                    <Reveal>
                        <p className="kicker text-[var(--xtra-blue)]">The full range</p>
                        <h1 className="mt-3 display-hero text-4xl sm:text-5xl lg:text-6xl">
                            {category === 'All' ? 'Shop all products' : category}
                        </h1>
                        <p className="mt-4 text-[var(--text-muted)] max-w-xl text-lg">
                            High-performance cleaning essentials for every corner of your home — Xtra clean, Xtra value, Xtra care.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Toolbar */}
            <div className="border-b border-[var(--border)] bg-white/80 backdrop-blur-md">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                    <div data-testid="category-filter-tabs" className="flex flex-nowrap lg:flex-wrap gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 lg:overflow-visible">
                        {CATEGORIES.map((c) => {
                            const on = category === c;
                            return (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    data-testid={`category-tab-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                                    aria-pressed={on}
                                    className={`shrink-0 whitespace-nowrap px-4 min-h-[44px] rounded-full text-sm font-semibold border transition-colors ${on ? 'bg-[var(--xtra-blue)] text-white border-[var(--xtra-blue)] shadow-[var(--shadow-tight)]' : 'bg-white text-[var(--text)] border-[var(--border)] hover:border-[var(--xtra-blue)] hover:text-[var(--xtra-blue)]'}`}
                                >{c}</button>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-3">
                        <form onSubmit={submitSearch} className="relative flex-1 lg:w-64">
                            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                            <Input data-testid="product-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search products" className="pl-10 pr-9 h-11 rounded-full bg-[var(--bg-soft)] border-transparent focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[var(--ring)]" />
                            {searchInput && (
                                <button type="button" onClick={clearSearch} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-black"><X className="h-4 w-4" /></button>
                            )}
                        </form>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger data-testid="sort-select" className="h-11 w-[160px] rounded-full"><SlidersHorizontal className="h-4 w-4 mr-1" /><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="featured">Featured</SelectItem>
                                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                                <SelectItem value="rating">Top rated</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Grid */}
            <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-[var(--radius-lg)]" />)}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-24">
                        <div className="h-16 w-16 rounded-full bg-[var(--bg-soft)] grid place-items-center mx-auto"><Search className="h-7 w-7 text-[var(--text-muted)]" /></div>
                        <p className="font-display text-xl font-bold mt-5">No products found</p>
                        <p className="text-[var(--text-muted)] mt-2">Try a different search or category.</p>
                        <button onClick={() => { clearSearch(); setCategory('All'); }} className="btn-outline mt-6 h-11 px-6">Reset filters</button>
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-[var(--text-muted)] mb-5"><span className="font-semibold text-black tabular-nums">{products.length}</span> product{products.length !== 1 ? 's' : ''}{search ? <> for “<span className="text-black font-medium">{search}</span>”</> : ''}</p>
                        <div data-testid="product-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                            {products.map((p, i) => (
                                <Reveal key={p.id} delay={(i % 4) * 0.05}>
                                    <ProductCard product={p} index={i} />
                                </Reveal>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
